import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CommunityService from '../lib/services/communityService';
import type { UserConnection } from '../types/index';
import { Spinner } from './Spinner';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';

// Criar uma instância do serviço
const communityService = new CommunityService();

type ConnectionTab = 'friends' | 'pending' | 'sent';

const UserConnectionsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<ConnectionTab>('friends');
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Buscar conexões do usuário conforme a aba ativa
  useEffect(() => {
    const fetchConnections = async () => {
      if (!user) return;
      
      setLoading(true);
      
      try {
        let result: UserConnection[] = [];
        
        switch (activeTab) {
          case 'friends':
            result = await communityService.getAcceptedConnections(user.id);
            break;
          case 'pending':
            // Buscar solicitações pendentes recebidas (onde o usuário atual é o destinatário)
            result = (await communityService.getUserConnections(user.id, 'pending'))
              .filter(conn => conn.connected_user_id === user.id);
            break;
          case 'sent':
            // Buscar solicitações pendentes enviadas (onde o usuário atual é o remetente)
            result = (await communityService.getUserConnections(user.id, 'pending'))
              .filter(conn => conn.user_id === user.id);
            break;
        }
        
        setConnections(result);
      } catch (error) {
        console.error(`Erro ao buscar ${activeTab}:`, error);
        toast.error(`Erro ao carregar ${getTabTitle(activeTab)}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConnections();
  }, [user, activeTab]);

  const handleAcceptRequest = async (connectionId: string) => {
    if (!user) return;
    
    setActionLoading(true);
    setActionLoadingId(connectionId);
    
    try {
      const result = await communityService.updateConnectionStatus(
        connectionId, 
        'accepted', 
        user.id
      );
      
      if (result.success) {
        toast.success('Solicitação aceita');
        
        // Atualizar lista sem recarregar
        setConnections(prev => prev.filter(conn => conn.id !== connectionId));
      } else {
        toast.error(result.error || 'Erro ao aceitar solicitação');
      }
    } catch (error) {
      console.error('Erro ao aceitar solicitação:', error);
      toast.error('Erro ao aceitar solicitação');
    } finally {
      setActionLoading(false);
      setActionLoadingId(null);
    }
  };

  const handleRejectRequest = async (connectionId: string) => {
    if (!user) return;
    
    setActionLoading(true);
    setActionLoadingId(connectionId);
    
    try {
      const result = await communityService.updateConnectionStatus(
        connectionId, 
        'rejected', 
        user.id
      );
      
      if (result.success) {
        toast.success('Solicitação rejeitada');
        
        // Atualizar lista sem recarregar
        setConnections(prev => prev.filter(conn => conn.id !== connectionId));
      } else {
        toast.error(result.error || 'Erro ao rejeitar solicitação');
      }
    } catch (error) {
      console.error('Erro ao rejeitar solicitação:', error);
      toast.error('Erro ao rejeitar solicitação');
    } finally {
      setActionLoading(false);
      setActionLoadingId(null);
    }
  };

  const handleCancelRequest = async (connectionId: string) => {
    if (!user) return;
    
    setActionLoading(true);
    setActionLoadingId(connectionId);
    
    try {
      const result = await communityService.removeConnection(
        connectionId, 
        user.id
      );
      
      if (result.success) {
        toast.success('Solicitação cancelada');
        
        // Atualizar lista sem recarregar
        setConnections(prev => prev.filter(conn => conn.id !== connectionId));
      } else {
        toast.error(result.error || 'Erro ao cancelar solicitação');
      }
    } catch (error) {
      console.error('Erro ao cancelar solicitação:', error);
      toast.error('Erro ao cancelar solicitação');
    } finally {
      setActionLoading(false);
      setActionLoadingId(null);
    }
  };

  const handleRemoveFriend = async (connectionId: string) => {
    if (!user) return;
    
    setActionLoading(true);
    setActionLoadingId(connectionId);
    
    try {
      // Encontrar a conexão atual para obter o ID do usuário conectado
      const currentConnection = connections.find(conn => conn.id === connectionId);
      
      if (!currentConnection || !currentConnection.connected_profile) {
        toast.error('Não foi possível identificar a conexão');
        return;
      }
      
      // Passar o ID da conexão em vez do ID do usuário conectado
      const result = await communityService.removeConnection(
        connectionId,
        user.id
      );
      
      if (result.success) {
        toast.success('Amigo removido');
        
        // Atualizar lista sem recarregar
        setConnections(prev => prev.filter(conn => conn.id !== connectionId));
      } else {
        toast.error(result.error || 'Erro ao remover amigo');
      }
    } catch (error) {
      console.error('Erro ao remover amigo:', error);
      toast.error('Erro ao remover amigo');
    } finally {
      setActionLoading(false);
      setActionLoadingId(null);
    }
  };

  const handleViewProfile = (username?: string) => {
    if (!username) return;
    navigate(`/profile/${username}`);
  };

  const handleSendMessage = (username?: string) => {
    if (!username) return;
    navigate(`/messages/${username}`);
  };

  const getTabTitle = (tab: ConnectionTab): string => {
    switch (tab) {
      case 'friends': return 'Amigos';
      case 'pending': return 'Solicitações Recebidas';
      case 'sent': return 'Solicitações Enviadas';
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <h2 className="text-2xl font-bold text-red-600 dark:text-red-400">Acesso Restrito</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Você precisa estar logado para acessar esta página.</p>
        <button
          onClick={() => navigate('/signin')}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          Fazer Login
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Meus Contatos"
        description="Gerencie suas conexões, amigos e solicitações no Boardverso."
      />
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold dark:text-gray-100">Meus Contatos</h1>
          
          <button
            onClick={() => navigate('/users/search')}
            className="mt-3 md:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            Buscar Usuários
          </button>
        </div>
        
        {/* Abas */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              {(['friends', 'pending', 'sent'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  {getTabTitle(tab)}
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Lista de conexões */}
        <div className="mt-6">
          {loading ? (
            <div className="flex flex-col items-center py-10">
              <Spinner size="large" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando contatos...</p>
            </div>
          ) : connections.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {connections.map(connection => (
                <div 
                  key={connection.id} 
                  className="bg-white rounded-lg shadow-md p-4 flex flex-col md:flex-row md:items-center justify-between dark:bg-gray-800 dark:border dark:border-gray-700"
                >
                  {/* Informações do usuário */}
                  <div className="flex items-center mb-4 md:mb-0">
                    <div className="flex-shrink-0 mr-4">
                      {connection.connected_profile?.avatar_url ? (
                        <img 
                          src={connection.connected_profile.avatar_url} 
                          alt={`Avatar de ${connection.connected_profile.username}`}
                          className="w-12 h-12 rounded-full"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center dark:bg-gray-700">
                          <span className="text-xl font-bold text-gray-600 dark:text-gray-300">
                            {connection.connected_profile?.username?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-medium dark:text-gray-100">
                        {connection.connected_profile?.full_name || connection.connected_profile?.username}
                      </h3>
                      {connection.connected_profile?.full_name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">@{connection.connected_profile.username}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Ações */}
                  <div className="flex flex-wrap gap-2">
                    {activeTab === 'friends' && (
                      <>
                        <button
                          onClick={() => handleViewProfile(connection.connected_profile?.username)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center dark:bg-blue-500 dark:hover:bg-blue-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Ver Perfil
                        </button>
                        
                        <button
                          onClick={() => handleSendMessage(connection.connected_profile?.username)}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm flex items-center dark:bg-purple-500 dark:hover:bg-purple-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                          </svg>
                          Mensagem
                        </button>
                        
                        <button
                          onClick={() => handleRemoveFriend(connection.id)}
                          disabled={actionLoading && actionLoadingId === connection.id}
                          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm flex items-center dark:bg-gray-700 dark:hover:bg-gray-600"
                        >
                          {actionLoading && actionLoadingId === connection.id ? (
                            <Spinner size="small" />
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M11 6a3 3 0 11-6 0 3 3 0 016 0zM14 17a6 6 0 00-12 0h12zM13 8a1 1 0 100 2h4a1 1 0 100-2h-4z" />
                              </svg>
                              Remover
                            </>
                          )}
                        </button>
                      </>
                    )}
                    
                    {activeTab === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAcceptRequest(connection.id)}
                          disabled={actionLoading && actionLoadingId === connection.id}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm flex items-center dark:bg-green-500 dark:hover:bg-green-600"
                        >
                          {actionLoading && actionLoadingId === connection.id ? (
                            <Spinner size="small" />
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Aceitar
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleRejectRequest(connection.id)}
                          disabled={actionLoading && actionLoadingId === connection.id}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm flex items-center dark:bg-red-500 dark:hover:bg-red-600"
                        >
                          {actionLoading && actionLoadingId === connection.id ? (
                            <Spinner size="small" />
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Recusar
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleViewProfile(connection.connected_profile?.username)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center dark:bg-blue-500 dark:hover:bg-blue-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Ver Perfil
                        </button>
                      </>
                    )}
                    
                    {activeTab === 'sent' && (
                      <>
                        <button
                          onClick={() => handleCancelRequest(connection.id)}
                          disabled={actionLoading && actionLoadingId === connection.id}
                          className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md text-sm flex items-center dark:bg-yellow-500 dark:hover:bg-yellow-600"
                        >
                          {actionLoading && actionLoadingId === connection.id ? (
                            <Spinner size="small" />
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                              </svg>
                              Cancelar
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleViewProfile(connection.connected_profile?.username)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center dark:bg-blue-500 dark:hover:bg-blue-600"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          Ver Perfil
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-medium text-gray-600 dark:text-gray-300">Nenhum registro encontrado</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                {activeTab === 'friends' && 'Você ainda não tem amigos adicionados.'}
                {activeTab === 'pending' && 'Você não tem solicitações de amizade pendentes.'}
                {activeTab === 'sent' && 'Você não enviou solicitações de amizade.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserConnectionsPage; 